import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { useUser } from '../context/UserContext';
import { connectAdmin } from '../api/apiConfig';
import { connectPublicUrl } from '../utils/connectUrls';
import { nfcSupported, writeNfcUrl } from '../utils/nfcWrite';
import { formatChileMobile } from '../utils/phoneChile';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const emptyEdit = {
  id: null,
  nombre: '',
  cargo: '',
  whatsapp: '',
  whatsapp_origen: '',
  email: '',
  origen_default: '',
  activo: 1,
};

function urlsDe(row) {
  const origin = window.location.origin;
  const src = String(row?.origen_default || '').trim();
  return {
    card: connectPublicUrl(origin, row.slug, { src }),
    kiosk: connectPublicUrl(origin, row.slug, { src, modoQr: true }),
  };
}

async function copyText(text) {
  const value = String(text || '');
  if (!value) return false;
  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* HTTP / permiso: cae al fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const ConnectTarjetas = () => {
  const { user } = useUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(null);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [qrRow, setQrRow] = useState(null);
  const [qrSrc, setQrSrc] = useState('');
  const [nfcMsg, setNfcMsg] = useState('');
  const [nfcBusy, setNfcBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const result = await connectAdmin(user, { accion: 'listar_tarjetas' });
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'No se pudieron cargar las tarjetas.' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!qrRow) {
      setQrSrc('');
      setNfcMsg('');
      setNfcBusy(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = urlsDe(qrRow).card;
        const dataUrl = await QRCode.toDataURL(url, { width: 280, margin: 1, errorCorrectionLevel: 'M' });
        if (!cancelled) setQrSrc(dataUrl);
      } catch {
        if (!cancelled) setQrSrc('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qrRow]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.nombre, r.slug, r.email, r.cargo].some((v) => String(v || '').toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const sync = async () => {
    if (!user) return;
    setSyncing(true);
    setMessage(null);
    try {
      const result = await connectAdmin(user, { accion: 'sync_lineas' });
      const total = result?.total_otorgamientos ?? result?.total_lineas ?? 0;
      const extra = [];
      if (result?.desactivados) extra.push(`${result.desactivados} sin operación en el año (inactivos)`);
      if (result?.reactivados) extra.push(`${result.reactivados} reactivados`);
      setMessage({
        type: 'ok',
        text: `Sincronizado: ${result?.creados ?? 0} nuevos, ${result?.existentes ?? 0} ya existían (${total} con otorgamientos en 12 meses)${extra.length ? `. ${extra.join('; ')}.` : '.'}`,
      });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'No se pudo sincronizar desde otorgamientos.' });
    } finally {
      setSyncing(false);
    }
  };

  const saveEdit = async (ev) => {
    ev.preventDefault();
    if (!user || !edit?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await connectAdmin(user, {
        accion: 'actualizar_tarjeta',
        id: edit.id,
        cargo: edit.cargo,
        ...(edit.whatsapp_origen === 'usuarios' ? {} : { whatsapp: edit.whatsapp }),
        email: edit.email,
        origen_default: edit.origen_default,
        activo: edit.activo ? 1 : 0,
      });
      setEdit(null);
      setMessage({ type: 'ok', text: 'Tarjeta actualizada.' });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'No se pudo guardar.' });
    } finally {
      setSaving(false);
    }
  };

  const writeNfcTag = async () => {
    if (!qrRow) return;
    const url = urlsDe(qrRow).card;
    if (!nfcSupported()) {
      setNfcMsg('Para grabar usa Chrome en un Android. El iPhone del cliente igual abre la tarjeta al acercarlo.');
      return;
    }
    setNfcBusy(true);
    setNfcMsg('Acerca la etiqueta al teléfono…');
    try {
      await writeNfcUrl(url);
      setNfcMsg('Etiqueta grabada.');
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setNfcMsg('No se pudo grabar. Usa una etiqueta regrabable y mantenla cerca.');
    } finally {
      setNfcBusy(false);
    }
  };

  const runClear = async () => {
    if (!user || !confirm) return;
    setClearing(true);
    setMessage(null);
    try {
      if (confirm.tipo === 'todas') {
        const result = await connectAdmin(user, { accion: 'limpiar_tarjetas', confirmar: true });
        setMessage({ type: 'ok', text: `Se eliminaron ${result?.eliminados ?? 0} tarjetas.` });
      } else {
        await connectAdmin(user, { accion: 'eliminar_tarjeta', id: confirm.id, confirmar: true });
        setMessage({ type: 'ok', text: `Se eliminó la tarjeta de ${confirm.nombre}.` });
      }
      setConfirm(null);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'No se pudo eliminar.' });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Connect — Tarjetas</h1>
        <p className="text-gray-600">Enlaces públicos por ejecutivo (sin login)</p>
        <Link to="/panel-marketing/connect-registros" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>
          Ver registros
        </Link>
      </div>

      <div className="seccion-datos">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-w-[220px]"
            placeholder="Buscar nombre, slug o correo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="btn-primary" disabled={syncing || loading} onClick={sync}>
            {syncing ? 'Sincronizando…' : 'Sincronizar desde otorgamientos'}
          </button>
          <button type="button" className="btn-primary" disabled={loading} onClick={load}>
            Recargar
          </button>
          <button
            type="button"
            className="text-sm py-2 px-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
            disabled={loading || clearing || rows.length === 0}
            onClick={() => setConfirm({ tipo: 'todas' })}
          >
            Limpiar todas
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4 max-w-3xl">
          Crea una tarjeta por cada ejecutivo con operaciones en <code>COMERCIAL.sf_otorgamientos</code> en el último año. Quienes no operaron quedan inactivos (el link público no abre). El slug no cambia al volver a sincronizar. El teléfono de Guardar contacto y WhatsApp sale de <code>MGC.USUARIOS</code> (Gestión de Usuarios).
        </p>

        {message && (
          <div
            className={`mb-4 text-sm rounded px-3 py-2 ${
              message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <AiOutlineLoading3Quarters className="animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="overflow-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Ejecutivo</th>
                  <th className="text-left px-3 py-2">Slug / link</th>
                  <th className="text-left px-3 py-2">Contacto</th>
                  <th className="text-left px-3 py-2">Origen</th>
                  <th className="text-left px-3 py-2">Estado</th>
                  <th className="text-left px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                      No hay tarjetas. Sincroniza desde otorgamientos para generarlas.
                    </td>
                  </tr>
                )}
                {filtered.map((r) => {
                  const { card, kiosk } = urlsDe(r);
                  return (
                    <tr key={r.id} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-800">{r.nombre}</div>
                        <div className="text-xs text-gray-500">{r.cargo}</div>
                      </td>
                      <td className="px-3 py-2">
                        <code className="text-xs bg-gray-50 px-1 rounded break-all">{card}</code>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                            onClick={async () => {
                              const ok = await copyText(kiosk);
                              setMessage({ type: ok ? 'ok' : 'error', text: ok ? `Link de ${r.nombre} copiado.` : 'No se pudo copiar.' });
                            }}
                          >
                            Copiar link kiosco
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                            onClick={async () => {
                              const ok = await copyText(card);
                              setMessage({ type: ok ? 'ok' : 'error', text: ok ? 'Link de tarjeta copiado.' : 'No se pudo copiar.' });
                            }}
                          >
                            Copiar tarjeta
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <div>{r.whatsapp ? formatChileMobile(r.whatsapp) : '—'}</div>
                        <div>{r.email || '—'}</div>
                        {!r.whatsapp && (
                          <div className="text-amber-700 mt-1">Sin teléfono en usuarios</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.origen_default || '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            r.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {r.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <button type="button" className="text-xs px-2 py-1 border rounded hover:bg-gray-50" onClick={() => setQrRow(r)}>
                            QR
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                            onClick={() =>
                              setEdit({
                                ...emptyEdit,
                                id: r.id,
                                nombre: r.nombre,
                                cargo: r.cargo || '',
                                whatsapp: formatChileMobile(r.whatsapp || ''),
                                whatsapp_origen: r.whatsapp_origen || '',
                                email: r.email || '',
                                origen_default: r.origen_default || '',
                                activo: r.activo ? 1 : 0,
                              })
                            }
                          >
                            Editar
                          </button>
                          <a className="text-xs px-2 py-1 border rounded hover:bg-gray-50" href={kiosk} target="_blank" rel="noreferrer">
                            Abrir
                          </a>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50"
                            onClick={() => setConfirm({ tipo: 'una', id: r.id, nombre: r.nombre, slug: r.slug })}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setEdit(null)}>
          <form className="bg-white rounded-xl w-full max-w-lg p-5 shadow-xl" onClick={(e) => e.stopPropagation()} onSubmit={saveEdit}>
            <h2 className="text-lg font-bold text-gray-800 mb-1">Editar tarjeta</h2>
            <p className="text-sm text-gray-500 mb-4">{edit.nombre}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
            <input className="w-full px-3 py-2 border rounded-lg text-sm mb-3" value={edit.cargo} onChange={(e) => setEdit({ ...edit, cargo: e.target.value })} />
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono (WhatsApp)</label>
            {edit.whatsapp_origen === 'usuarios' ? (
              <p className="text-sm text-gray-600 mb-3">
                {edit.whatsapp || 'Sin teléfono'}
                <span className="block text-xs text-gray-500 mt-1">
                  Se edita en{' '}
                  <Link to="/usuarios/gestion" className="text-blue-700 underline">
                    Gestión de Usuarios
                  </Link>
                  . Guardar contacto y WhatsApp de la tarjeta usan este número.
                </span>
              </p>
            ) : (
              <>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm mb-1"
                  value={edit.whatsapp}
                  onChange={(e) => setEdit({ ...edit, whatsapp: formatChileMobile(e.target.value) })}
                  onFocus={() => setEdit((prev) => ({ ...prev, whatsapp: formatChileMobile(prev.whatsapp) }))}
                  inputMode="tel"
                  maxLength={14}
                  placeholder="56 9 1234 5678"
                />
                <p className="text-xs text-gray-500 mb-3">
                  No hay ficha en usuarios para este nombre. Cárgalo en{' '}
                  <Link to="/usuarios/gestion" className="text-blue-700 underline">
                    Gestión de Usuarios
                  </Link>{' '}
                  o déjalo aquí como respaldo.
                </p>
              </>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
            <input className="w-full px-3 py-2 border rounded-lg text-sm mb-3" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
            <label className="block text-sm font-medium text-gray-700 mb-1">Origen por defecto</label>
            <input className="w-full px-3 py-2 border rounded-lg text-sm mb-3" value={edit.origen_default} onChange={(e) => setEdit({ ...edit, origen_default: e.target.value })} placeholder="FEXMIN26" />
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
              <input type="checkbox" checked={!!edit.activo} onChange={(e) => setEdit({ ...edit, activo: e.target.checked ? 1 : 0 })} />
              Activo (visible en el link público)
            </label>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-3 py-2 text-sm border rounded-lg" onClick={() => setEdit(null)}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary text-sm py-2 px-3" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => !clearing && setConfirm(null)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-800 mb-2">¿Estás seguro?</h2>
            {confirm.tipo === 'todas' ? (
              <p className="text-sm text-gray-600 mb-4">
                Se van a eliminar <strong>todas las tarjetas</strong> ({rows.length}). Los links públicos dejarán de funcionar. Los leads y la actividad se conservan. Esta acción no se puede deshacer.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                Se va a eliminar la tarjeta de <strong>{confirm.nombre}</strong>
                {confirm.slug ? (
                  <>
                    {' '}
                    (<code className="text-xs">/c/{confirm.slug}</code>)
                  </>
                ) : null}
                . El link público dejará de funcionar. Esta acción no se puede deshacer.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-3 py-2 text-sm border rounded-lg" disabled={clearing} onClick={() => setConfirm(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={clearing}
                onClick={runClear}
              >
                {clearing ? 'Eliminando…' : confirm.tipo === 'todas' ? 'Sí, eliminar todas' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrRow && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setQrRow(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-800 mb-1">{qrRow.nombre}</h2>
            <p className="text-xs text-gray-500 mb-3 break-all">{urlsDe(qrRow).card}</p>
            {qrSrc ? <img src={qrSrc} alt="QR" className="mx-auto w-56 h-56" /> : <p className="text-sm text-gray-400 py-8">Generando QR…</p>}
            <button type="button" className="btn-primary mt-4 text-sm py-2 px-3 w-full" onClick={writeNfcTag} disabled={nfcBusy}>
              {nfcBusy ? 'Esperando etiqueta…' : 'Grabar etiqueta NFC'}
            </button>
            {nfcMsg ? <p className="text-xs text-gray-600 mt-2">{nfcMsg}</p> : null}
            <button type="button" className="mt-3 text-sm py-2 px-3 border rounded-lg w-full" onClick={() => setQrRow(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectTarjetas;
